import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { CATEGORIES, QUESTION_TAGS, getCategoryColor, getTagColor, getStatusColor } from '../utils/constants';
import { exportQuestionsToExcel } from '../utils/excelExport';
import supabase from '../lib/supabase';

const { FiArrowLeft, FiSearch, FiLoader, FiPlus, FiHelpCircle, FiDownload, FiCheckCircle, FiAlertTriangle } = FiIcons;

const QuestionsPage = ({ onNavigate, initialFilter = null }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState(initialFilter?.status || '');
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportMessage, setExportMessage] = useState({ type: '', text: '' });

  const statuses = ['Open', 'In Progress', 'Answered'];

  useEffect(() => {
    fetchQuestions();
  }, []);

  // Set initial filter if provided
  useEffect(() => {
    if (initialFilter?.status) {
      setStatusFilter(initialFilter.status);
    }
  }, [initialFilter]);

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('questions_kb2024')
        .select('id, title, category, status, tags, asked_by, created_at, description')
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      setQuestions(data || []);
    } catch (error) {
      console.error('Error fetching questions:', error);
      setError('Unable to load questions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filteredQuestions = questions.filter(question => {
    const matchesSearch = question.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !categoryFilter || question.category === categoryFilter;
    const matchesTag = !tagFilter || (question.tags && question.tags.includes(tagFilter));
    const matchesStatus = !statusFilter || question.status === statusFilter;
    return matchesSearch && matchesCategory && matchesTag && matchesStatus;
  });

  const handleExportToExcel = async () => {
    try {
      setExporting(true);
      setExportMessage({ type: '', text: '' });

      // Prepare filter information for the export
      const appliedFilters = {
        search: searchTerm,
        category: categoryFilter,
        tag: tagFilter,
        status: statusFilter
      };

      // Export filtered questions to Excel
      const result = exportQuestionsToExcel(filteredQuestions, appliedFilters);

      if (result.success) {
        setExportMessage({
          type: 'success',
          text: `Successfully exported ${result.recordCount} questions to ${result.filename}`
        });
        
        // Clear message after 5 seconds
        setTimeout(() => {
          setExportMessage({ type: '', text: '' });
        }, 5000);
      } else {
        throw new Error(result.error || 'Export failed');
      }
    } catch (error) {
      console.error('Export error:', error);
      setExportMessage({
        type: 'error',
        text: `Export failed: ${error.message}`
      });
      
      // Clear message after 5 seconds
      setTimeout(() => {
        setExportMessage({ type: '', text: '' });
      }, 5000);
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const clearFilters = () => {
    setSearchTerm('');
    setCategoryFilter('');
    setTagFilter('');
    setStatusFilter('');
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="flex items-center gap-3">
            <SafeIcon icon={FiLoader} className="w-8 h-8 text-blue-600 animate-spin" />
            <span className="text-lg text-gray-600">Loading questions...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <button
          onClick={() => onNavigate('dashboard')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors duration-200"
        >
          <SafeIcon icon={FiArrowLeft} className="w-4 h-4" />
          Back to Dashboard
        </button>

        <div className="flex items-center gap-3 mb-2">
          <SafeIcon icon={FiHelpCircle} className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-900">
            {statusFilter === 'Open' ? 'Open Questions' : 'All Questions'}
          </h1>
        </div>

        <p className="text-gray-600">
          {statusFilter === 'Open' 
            ? 'Questions that are currently open and awaiting answers'
            : 'Browse and search through all community questions'
          }
        </p>

        {statusFilter === 'Open' && (
          <div className="mt-3 flex items-center gap-2">
            <span className="px-3 py-1 text-sm font-medium bg-red-100 text-red-800 rounded-full">
              Filtered by: Open Status
            </span>
            <button
              onClick={clearFilters}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              Clear all filters
            </button>
          </div>
        )}
      </motion.div>

      {error && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6"
        >
          <p className="text-red-700">{error}</p>
          <button
            onClick={fetchQuestions}
            className="mt-2 text-red-600 hover:text-red-700 font-medium"
          >
            Try Again
          </button>
        </motion.div>
      )}

      {/* Export Message */}
      {exportMessage.text && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`mb-6 p-4 rounded-lg border ${
            exportMessage.type === 'success' 
              ? 'bg-green-50 border-green-200 text-green-700' 
              : 'bg-red-50 border-red-200 text-red-700'
          }`}
        >
          <div className="flex items-center gap-2">
            <SafeIcon 
              icon={exportMessage.type === 'success' ? FiCheckCircle : FiAlertTriangle} 
              className="w-4 h-4" 
            />
            <span className="text-sm font-medium">{exportMessage.text}</span>
          </div>
        </motion.div>
      )}

      {/* Action Buttons */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="mb-6 flex flex-col sm:flex-row gap-4"
      >
        <button
          onClick={() => onNavigate('question-form')}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200"
        >
          <SafeIcon icon={FiPlus} className="w-5 h-5" />
          Ask a Question
        </button>

        <button
          onClick={handleExportToExcel}
          disabled={exporting || filteredQuestions.length === 0}
          className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {exporting ? (
            <>
              <SafeIcon icon={FiLoader} className="w-5 h-5 animate-spin" />
              Exporting...
            </>
          ) : (
            <>
              <SafeIcon icon={FiDownload} className="w-5 h-5" />
              Export to Excel ({filteredQuestions.length})
            </>
          )}
        </button>
      </motion.div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8"
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-4">
          <div className="xl:col-span-1">
            <div className="relative">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search questions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Categories</option>
              {CATEGORIES.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>

          <div>
            <select
              value={tagFilter}
              onChange={(e) => setTagFilter(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Tags</option>
              {QUESTION_TAGS.map(tag => (
                <option key={tag} value={tag}>{tag}</option>
              ))}
            </select>
          </div>

          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Status</option>
              {statuses.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>
        </div>
      </motion.div>

      {/* Questions List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-xl shadow-sm border border-gray-100"
      >
        <div className="p-6 border-b border-gray-100">
          <h2 className="text-xl font-semibold text-gray-900">
            {filteredQuestions.length} Question{filteredQuestions.length !== 1 ? 's' : ''}
            {statusFilter === 'Open' && (
              <span className="text-red-600"> (Open)</span>
            )}
          </h2>
        </div>

        <div className="divide-y divide-gray-100">
          {filteredQuestions.map((question, index) => (
            <motion.div
              key={question.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + index * 0.05 }}
              className="p-6 hover:bg-gray-50 transition-colors duration-200 cursor-pointer"
              onClick={() => onNavigate('question-details', question.id)}
            >
              <div className="flex justify-between items-start mb-3">
                <h3 className="text-lg font-medium text-gray-900 hover:text-blue-600 transition-colors">
                  {question.title}
                </h3>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(question.status)}`}>
                  {question.status}
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getCategoryColor(question.category)}`}>
                  {question.category}
                </span>
                {question.tags && question.tags.length > 0 && (
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTagColor(question.tags[0])}`}>
                    {question.tags[0]}
                  </span>
                )}
              </div>

              <div className="text-sm text-gray-600">
                Asked by {question.asked_by} • {formatDate(question.created_at)}
              </div>
            </motion.div>
          ))}
        </div>

        {filteredQuestions.length === 0 && (
          <div className="p-12 text-center">
            <SafeIcon icon={FiHelpCircle} className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No questions found</h3>
            <p className="text-gray-600 mb-4">
              {searchTerm || categoryFilter || tagFilter || statusFilter
                ? "No questions found matching your filters."
                : "No questions have been posted yet."
              }
            </p>
            <button
              onClick={() => onNavigate('question-form')}
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
            >
              <SafeIcon icon={FiPlus} className="w-4 h-4" />
              Ask the First Question
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default QuestionsPage;